import { useState } from "react";
import { toast } from "sonner";
import { Copy, Upload, MessageCircle, CheckCircle2, FileText, Landmark } from "lucide-react";
import { api } from "../lib/api";
import { fmtARS } from "../lib/utils-app";

const WA_NUMBERS = [
  { label: "+54 3821 452181", wa: "543821452181", testid: "manual-wa-1" },
  { label: "+54 3821 450522", wa: "543821450522", testid: "manual-wa-2" },
];

function buildMessage(o, categoryLabel, identifierLabel) {
  return [
    `Hola! Hice un pedido de ${categoryLabel}:`,
    `Pedido: ${o.order_id}`,
    `Producto: ${o.quantity} x ${o.product_name}`,
    `${identifierLabel}: ${o.player_id}`,
    `Total transferido: ${fmtARS(o.amount_ars)}`,
    `Cuenta: ${o.user_email}`,
    `Comprobante: ${o.receipt_url}`,
  ].join("\n");
}

function SentOrder({ order, theme, categoryLabel, identifierLabel }) {
  const msg = encodeURIComponent(buildMessage(order, categoryLabel, identifierLabel));
  return (
    <div data-testid="manual-order-created" className="space-y-4">
      <div className="flex items-center gap-2 text-emerald-400 text-sm">
        <CheckCircle2 className="w-5 h-5" /> Pedido {order.order_id} creado. Envianos la info por WhatsApp para procesarlo.
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {WA_NUMBERS.map((n) => (
          <a
            key={n.wa}
            href={`https://wa.me/${n.wa}?text=${msg}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={n.testid}
            className="flex items-center justify-center gap-2 px-4 py-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
            style={{ transition: "background-color 160ms ease" }}
          >
            <MessageCircle className="w-5 h-5" /> Enviar a {n.label}
          </a>
        ))}
      </div>
      <a href={order.receipt_url} target="_blank" rel="noopener noreferrer" className="text-xs underline" style={{ color: theme.primary }} data-testid="manual-receipt-link">
        Ver comprobante subido
      </a>
    </div>
  );
}

export default function ManualTransferCheckout({ alias, selected, quantity, playerId, nickname, total, canSubmit, user, onLogin, theme, categoryLabel = "Evolutivos", identifierLabel = "ID Free Fire" }) {
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [order, setOrder] = useState(null);

  async function submit() {
    if (!user) return onLogin();
    if (!file) return toast.error("Adjuntá el comprobante de la transferencia");
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("package_id", selected.id);
      fd.append("player_id", playerId);
      fd.append("quantity", String(quantity));
      if (nickname) fd.append("player_nickname", nickname);
      fd.append("receipt", file);
      const { data } = await api.post("/orders/manual", fd, { timeout: 90000 });
      setOrder(data);
      toast.success("Pedido creado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando el pedido");
    } finally {
      setSending(false);
    }
  }

  if (order) return <SentOrder order={order} theme={theme} categoryLabel={categoryLabel} identifierLabel={identifierLabel} />;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4 flex flex-wrap items-center gap-3" style={{ borderColor: `${theme.primary}55`, background: "#080808" }}>
        <Landmark className="w-5 h-5" style={{ color: theme.primary }} />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">1 · Transferí {selected ? fmtARS(total) : "el total"} al alias</div>
          <div className="font-mono text-xl text-white" data-testid="transfer-alias">{alias}</div>
        </div>
        <button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(alias);
              toast.success("Alias copiado");
            } catch {
              toast.info(`Copiá el alias: ${alias}`);
            }
          }}
          data-testid="copy-alias-btn"
          className="ml-auto px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1 text-white"
        >
          <Copy className="w-3.5 h-3.5" /> Copiar
        </button>
      </div>

      <label className="block rounded-lg border border-dashed p-4 cursor-pointer hover:bg-white/[0.02]" style={{ borderColor: `${theme.primary}66` }}>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">2 · Adjuntá el comprobante</div>
        <div className="flex items-center gap-2 text-sm text-white">
          {file ? <FileText className="w-4 h-4" style={{ color: theme.primary }} /> : <Upload className="w-4 h-4" style={{ color: theme.primary }} />}
          <span className="truncate" data-testid="receipt-file-name">{file ? file.name : "Tocá para elegir una imagen o PDF (máx. 10 MB)"}</span>
        </div>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
          className="hidden"
          data-testid="receipt-input"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
      </label>

      <button
        data-testid="manual-submit-btn"
        onClick={submit}
        disabled={user ? !canSubmit || !file || sending : false}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-md text-base font-bold text-black disabled:opacity-50"
        style={{ background: theme.primary }}
      >
        <CheckCircle2 className="w-5 h-5" />
        {user ? (sending ? "Enviando..." : "3 · Confirmar pedido") : "Iniciar sesión y confirmar"}
      </button>
    </div>
  );
}
